import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Mic, Eye, Radio, Users } from 'lucide-react';
import { cn } from '../lib/utils';

interface HomePageProps {
  className?: string;
}

export const HomePage: React.FC<HomePageProps> = ({ className }) => {
  return (
    <div className={cn("max-w-4xl mx-auto p-6 space-y-8", className)}>
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
            <Radio className="h-6 w-6 text-primary-foreground" />
          </div>
          <h1 className="text-4xl font-bold">Real-time Audio Transcription</h1>
        </div>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Stream your audio with live transcription or join someone else's stream to see real-time subtitles
        </p>
      </div>

      {/* Main Options */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Stream Option */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
              <Mic className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Start Streaming</CardTitle>
            <CardDescription className="text-base">
              Share your audio with real-time transcription for viewers
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                Generate a unique room ID for viewers
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                Stream audio using WebM/Opus format
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                Real-time transcription with AI
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                Monitor connection and audio levels
              </li>
            </ul>
            <Link to="/streamer" className="block">
              <Button className="w-full" size="lg">
                <Mic className="h-4 w-4 mr-2" />
                Start Streaming
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* View Option */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-secondary/20 transition-colors">
              <Eye className="h-8 w-8 text-secondary-foreground" />
            </div>
            <CardTitle className="text-2xl">Join Stream</CardTitle>
            <CardDescription className="text-base">
              Watch live audio streams with real-time subtitles
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-secondary-foreground"></div>
                Join using a room ID from streamer
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-secondary-foreground"></div>
                See live subtitles with timestamps
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-secondary-foreground"></div>
                Automatic subtitle hiding after 10 seconds
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-secondary-foreground"></div>
                Auto-reconnection on connection loss
              </li>
            </ul>
            <Link to="/viewer" className="block">
              <Button variant="secondary" className="w-full" size="lg">
                <Eye className="h-4 w-4 mr-2" />
                Join Stream
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Features Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            How It Works
          </CardTitle>
          <CardDescription>
            Simple steps to get started with real-time audio transcription
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">For Streamers</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    1
                  </div>
                  <div className="text-sm">
                    <div className="font-medium">Start Streaming</div>
                    <div className="text-muted-foreground">Click "Start Streaming" and allow microphone access</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    2
                  </div>
                  <div className="text-sm">
                    <div className="font-medium">Share Room ID</div>
                    <div className="text-muted-foreground">Copy and share your unique 8-character room ID</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    3
                  </div>
                  <div className="text-sm">
                    <div className="font-medium">Start Speaking</div>
                    <div className="text-muted-foreground">Your audio will be transcribed in real-time for viewers</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">For Viewers</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    1
                  </div>
                  <div className="text-sm">
                    <div className="font-medium">Join Stream</div>
                    <div className="text-muted-foreground">Click "Join Stream" and enter the room ID</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    2
                  </div>
                  <div className="text-sm">
                    <div className="font-medium">Watch Subtitles</div>
                    <div className="text-muted-foreground">See live transcriptions appear with timestamps</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    3
                  </div>
                  <div className="text-sm">
                    <div className="font-medium">Enjoy the Stream</div>
                    <div className="text-muted-foreground">Subtitles automatically hide after 10 seconds</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Technical Info */}
      <Card>
        <CardHeader>
          <CardTitle>Technical Features</CardTitle>
          <CardDescription>
            Powered by modern web technologies and AI
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="font-medium mb-1">Audio Format</div>
              <div className="text-muted-foreground">WebM/Opus for efficient streaming</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="font-medium mb-1">AI Transcription</div>
              <div className="text-muted-foreground">Cloudflare Workers AI with Whisper</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="font-medium mb-1">Real-time</div>
              <div className="text-muted-foreground">WebSocket connections for low latency</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};